import { reactivationSituation } from './commercial.mjs';
import { isProspectingSuggestion } from './task-routing.mjs';
import { todayParis } from '../utils/calendar.mjs';
import { isBlockedForProspecting } from './crm-v2-policy.mjs';
const id = value => String(value?._id || value?.id || value || '');
const day = value => {
  const text = value instanceof Date ? value.toISOString() : String(value || '');
  const match = text.match(/^(\d{4})-?(\d{2})-?(\d{2})(?:$|T)/);
  if (!match) return null;
  const result = `${match[1]}-${match[2]}-${match[3]}`;
  const date = new Date(`${result}T12:00:00Z`);
  return Number.isFinite(date.getTime()) && date.toISOString().slice(0,10) === result ? result : null;
};
const closed = task => task.done || task.cancelled || /^(effectuée|effectuee|annulée|annulee|done|cancelled)$/i.test(task.status || '');
const independent = task => ['follow-quote','cv-follow'].includes(task.automationKey) || task.quoteId;
const time = row => Date.parse(row.createdAt || row.at) || 0;

// Every loaded establishment receives either one card or an explicit reason
// for being absent. Filters are applied by the caller, never by this audit.
export function auditProspectingQueue(clients, tasks, today = todayParis(), results = []) {
  const groups = new Map();
  for (const client of clients) {
    const identity = id(client.client_id || client.establishment_id || client._id || client.id);
    const group = groups.get(identity) || [];
    group.push(client); groups.set(identity, group);
  }
  const cards = [], excluded = [], issues = [];
  for (const [identity, records] of groups) {
    const client = records[0];
    const aliases = new Set(records.flatMap(record => [record.id,record.client_id,record.establishment_id,record._id]).map(id).filter(Boolean));
    const linked = row => [row.clientId,row.establishment_id,row.client].some(value => value && aliases.has(id(value)));
    const exclude = reason => excluded.push({client,identity,reason});
    if (records.some(record => isBlockedForProspecting(record) || record.prospectingBlocked || record.commercialFollowUp?.prospectingExcluded || record.commercialFollowUp?.prospectingOpposition)) { exclude('blocked'); continue; }
    const clientTasks = tasks.filter(linked);
    const active = clientTasks.filter(task => !closed(task));
    const calls = results.filter(linked).filter(result => !result.cancelled && !result.voided && !result.isCancelled && !/annul|cancel/i.test(String(result.status || ""))).sort((a,b)=>time(b)-time(a));
    const lastCall = calls[0];
    const noFollowUp = Boolean(lastCall?.noFollowUp || lastCall?.details?.noFollowUp);
    const futurePlan = active.filter(task => (task.promised || ['promised-call','future-need'].includes(task.automationKey) || task.plannedFromProspecting) && day(task.due_date_original || task.date) > today).sort((a,b)=>time(b)-time(a))[0];
    const obsolete = task => futurePlan && task.id !== futurePlan.id && (!task.resultId || task.resultId !== futurePlan.resultId) && !task.manuallyModified && !task.manualEditedAt && !independent(task) && time(task) < time(futurePlan);
    const followUps = active.filter(task => isProspectingSuggestion(task) && !obsolete(task) && !(noFollowUp && !independent(task)));
    const due = followUps.filter(task => day(task.due_date_original || task.date) && day(task.due_date_original || task.date) <= today).sort((a,b)=>day(a.due_date_original || a.date).localeCompare(day(b.due_date_original || b.date)));
    if (due.length) {
      cards.push({client,reasons:[...new Set(due.map(task=>task.title).filter(Boolean))],due:day(due[0].due_date_original || due[0].date),kind:'follow-up'}); continue;
    }
    if (noFollowUp) { exclude('no-follow-up'); continue; }
    const nextDate = day(client.next_action_date || client.nextAction);
    const relevant = active.filter(task => !obsolete(task));
    if (futurePlan || followUps.some(task=>day(task.due_date_original || task.date) > today) || nextDate > today) { exclude('future'); continue; }
    if (relevant.some(task => !isProspectingSuggestion(task) && !['manager','invoice'].includes(task.category))) { exclude('planned-in-day'); continue; }
    if (relevant.length) issues.push({client,reason:'missing-task-date'});
    const reactivation = reactivationSituation(client,today);
    if (reactivation) {
      const reasons=['À réactiver',...reactivation.reasons];
      if(reactivation.inactivity) reasons.push(`${reactivation.inactivity.days} jours sans commande`);
      cards.push({client,reasons:[...new Set(reasons)],due:nextDate || today,reactivation:true,kind:'reactivation'}); continue;
    }
    const contacted = client.lastCallAt || client.lastCallResult || client.lastContactAt || calls.length || clientTasks.some(task=>closed(task) && !task.cancelled && ['call','appointment'].includes(task.category));
    if (!contacted && !nextDate && !followUps.length) { cards.push({client,reasons:[],due:null,kind:'uncontacted'}); continue; }
    cards.push({client,reasons:[relevant.length ? 'Date de relance à compléter' : 'Suivi à planifier'],due:nextDate || today,kind:'unplanned'});
    issues.push({client,reason:'unplanned-follow-up'});
  }
  for(const task of tasks.filter(task=>!closed(task)&&isProspectingSuggestion(task))) {
    if(!clients.some(client=>[client.id,client.client_id,client.establishment_id,client._id].map(id).filter(Boolean).includes(id(task.clientId || task.establishment_id)))) issues.push({task,reason:'unmatched-establishment'});
  }
  cards.sort((a,b)=>(a.due || '9999').localeCompare(b.due || '9999'));
  return {cards,excluded,issues,total:groups.size};
}
export function prospectingCards(clients,tasks,today= todayParis(),results=[]) {
  return auditProspectingQueue(clients,tasks,today,results).cards;
}

export function matchingProspectingCards(cards, clients) {
  const aliases = new Set(clients.flatMap(client=>[client.id,client._id,client.client_id,client.establishment_id]).map(id).filter(Boolean));
  return cards.filter(({client})=>[client.id,client._id,client.client_id,client.establishment_id].map(id).some(value=>value && aliases.has(value)));
}
